import React, { useState, useEffect } from 'react';
import { Layout, theme, message, Avatar, Divider, Grid } from 'antd';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ProLayout, PageContainer } from '@ant-design/pro-components';
import { Link } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { UserOutlined, SettingOutlined, LogoutOutlined, EllipsisOutlined } from '@ant-design/icons';

import packageJson from '../../../package.json';
import { useAuth } from '../../contexts/AuthContext';
import { useLayout } from '../../contexts/LayoutContext';
import { useRoute } from '../../contexts/RouteContext';
import { useLoading } from '../../contexts/LoadingContext';
import UserNav from '../UserNav/UserNav';
import CompanySelector from '../Common/CompanySelector/CompanySelector';
import SectionSelector from '../Common/SectionSelector/SectionSelector';
import {
  BreadcrumbDropdown,
  buildSectionDropdownItems,
  buildSubPageDropdownItems,
  deriveBreadcrumbs,
} from '../Common/Breadcrumb';
import LoadingOverlay from '../Common/LoadingOverlay/LoadingOverlay';
import BugReporterButton from '../BugReporter/BugReporterButton';
import './MainLayout.scss';

const { Content } = Layout;
const { useBreakpoint } = Grid;
const companyPaths = ['borrowing-base', 'dealers', 'floorplans', 'imports', 'loans'];
const MainLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const { heading, extraContent, headLoading, breadcrumbs } = useLayout();
  const { routesConfig } = useRoute();
  const { isLoading, loadingMessage } = useLoading();
  const location = useLocation();
  const { token } = theme.useToken();
  const { id } = useParams();
  const navigate = useNavigate();
  const screens = useBreakpoint();

  // Determine if we're on mobile/tablet (below md breakpoint - 768px)
  const isMobileOrTablet = !screens.md;

  // State to control the mobile drawer open/close
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Scroll to active menu item when sidebar opens on mobile
  useEffect(() => {
    if (isMobileOrTablet && mobileMenuOpen) {
      // Small delay to ensure menu is rendered
      const timer = setTimeout(() => {
        const activeMenuItem = document.querySelector(
          '.ant-menu-item-selected, .ant-pro-base-menu-menu-item-selected'
        );
        if (activeMenuItem) {
          activeMenuItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [mobileMenuOpen, isMobileOrTablet]);

  // Helper function to close sidebar on mobile after clicking a link
  const closeSidebarOnMobile = () => {
    if (isMobileOrTablet) {
      // Close the mobile drawer overlay
      setMobileMenuOpen(false);
      setCollapsed(true);
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('sidebar-collapsed', 'true');
      }
    }
  };

  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return false;
    }
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved ? JSON.parse(saved) : false;
  });

  // Get layout styles from theme
  const { colorBgContainer, borderRadiusLG } = token;
  const segments = location.pathname.split('/');

  // App version from environment
  const appVersion = packageJson.version || '1.0.0';

  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 768;
      if (isMobile && !collapsed) {
        setCollapsed(true);
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem('sidebar-collapsed', 'true');
        }
      }
    };

    const timer = setTimeout(handleResize, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 768;
      if (isMobile && !collapsed) {
        setCollapsed(true);
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem('sidebar-collapsed', 'true');
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [collapsed]);

  return (
    <ProLayout
      contentStyle={{ backgroundColor: '#ffffff' }}
      fixedHeader={true}
      menuDataRender={mdata => mdata}
      route={routesConfig}
      location={location}
      breadcrumbRender={false}
      fixSiderbar={true}
      splitMenus={true}
      suppressSiderWhenMenuEmpty={false}
      // Control collapsed state for mobile drawer
      collapsed={isMobileOrTablet ? !mobileMenuOpen : collapsed}
      logo={
        <img
          className="header-logo"
          style={{ maxWidth: '75px', marginBottom: '4px' }}
          src={'/LHPH_Logo.png'}
          alt={'LHPH Logo'}
        />
      }
      headerTitleRender={(logo, title) => <Link to="/">{logo}</Link>}
      actionsRender={props => {
        // Only show right-side logo on mobile/tablet (below 768px)
        if (isMobileOrTablet) {
          return [
            <div key="header-right-content" className="header-right-content">
              <img src="/LHPH_Logo.png" alt="LHPH Logo" className="header-right-logo" />
            </div>,
          ];
        }
        return [];
      }}
      menuHeaderRender={(logo, title, sprops) => {
        if (sprops?.mode === 'horizontal') {
          return (
            <div
              style={{
                height: 'auto',
                lineHeight: '12px',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Link to={`${''}`}>{logo}</Link>
              {user && <UserNav />}
            </div>
          );
        }
        // Don't render anything special for sidebar header - profile will be in menuContentRender
        return null;
      }}
      menuContentRender={(props, defaultDom) => {
        // Only show mobile profile section when sidebar drawer is open on mobile/tablet
        if (isMobileOrTablet && mobileMenuOpen) {
          // Check if we should show company selector (must have path + ID, like desktop)
          const hasCompanyPath = companyPaths.some(path => segments.includes(path));
          const hasIdInUrl = segments.some(seg => /^\d+$/.test(seg));

          return (
            <>
              <div className="mobile-menu-header">
                <div className="mobile-avatar-section">
                  <Avatar
                    size={40}
                    icon={<UserOutlined />}
                    style={{ backgroundColor: '#233867' }}
                  />
                  <div className="mobile-user-info">
                    <span className="mobile-user-name">
                      {user?.firstName} {user?.lastName}
                    </span>
                    <span className="mobile-user-username">@{user?.username}</span>
                  </div>
                </div>
                <div className="mobile-profile-links">
                  <Link
                    to="/user/profile"
                    className="mobile-profile-link"
                    onClick={closeSidebarOnMobile}
                  >
                    <UserOutlined /> Profile
                  </Link>
                  <Link
                    to="/user/profile"
                    className="mobile-profile-link"
                    onClick={closeSidebarOnMobile}
                  >
                    <SettingOutlined /> Settings
                  </Link>
                  <span
                    className="mobile-profile-link"
                    onClick={() => {
                      closeSidebarOnMobile();
                      logout()
                        .then(() => navigate('/sign-in'))
                        .catch(error => {
                          console.error('Logout error:', error);
                          message.error('Failed to logout. Please try again.');
                        });
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <LogoutOutlined /> Logout
                  </span>
                </div>
              </div>
              <div className="mobile-section-selector">
                <SectionSelector collapsed={false} onNavigate={closeSidebarOnMobile} />
              </div>
              {hasCompanyPath && hasIdInUrl && (
                <div className="mobile-company-selector">
                  <CompanySelector collapsed={collapsed} />
                </div>
              )}
              {defaultDom}
            </>
          );
        }
        return defaultDom;
      }}
      menu={{
        type: 'sub',
        defaultOpenAll: false,
        autoClose: false,
      }}
      siderWidth={270}
      layout={'mix'}
      menuItemRender={(item, dom) => {
        // Logout: hidden in sidebar (shown in footer), but visible in top nav overflow
        if (item.path === '/logout') {
          return (
            <span
              onClick={e => {
                e.preventDefault();
                closeSidebarOnMobile();
                logout()
                  .then(() => navigate('/sign-in'))
                  .catch(error => {
                    console.error('Logout error:', error);
                    message.error('Failed to logout. Please try again.');
                  });
              }}
            >
              {dom}
            </span>
          );
        }
        // Handle external Swagger links: the reports generator (lhph-reports)
        // and the main API's own /api-docs.
        if (item.path === '/admin/swagger' || item.path === '/admin/api-swagger') {
          const swaggerUrl =
            item.path === '/admin/swagger'
              ? process.env.REACT_APP_REPORTS_SWAGGER_URL || 'http://localhost:8080/swagger'
              : process.env.REACT_APP_API_SWAGGER_URL || 'http://localhost:9090/api-docs';
          return (
            <a
              href={swaggerUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={closeSidebarOnMobile}
            >
              {dom}
            </a>
          );
        }
        return (
          <Link to={item.path || '/'} onClick={closeSidebarOnMobile}>
            {dom}
          </Link>
        );
      }}
      subMenuItemRender={(item, dom) => {
        // Make section headers (Loans, Dealers, Floorplans, etc.) clickable on mobile
        // These navigate to the root section URL
        const sectionPaths: Record<string, string> = {
          '/loans': '/loans',
          '/dealers': '/dealers',
          '/floorplans': '/floorplans',
          '/borrowing-base': '/borrowing-base',
          '/imports': '/imports',
        };

        const rootPath = sectionPaths[item.path || ''];
        if (rootPath && isMobileOrTablet) {
          return (
            <span
              onClick={e => {
                // Navigate to root section
                navigate(rootPath);
                closeSidebarOnMobile();
              }}
              style={{ cursor: 'pointer' }}
            >
              {dom}
            </span>
          );
        }
        return dom;
      }}
      footerRender={false}
      menuFooterRender={() => (
        <div className="sidebar-footer">
          <div
            className="sidebar-logout"
            onClick={() => {
              closeSidebarOnMobile();
              logout()
                .then(() => navigate('/sign-in'))
                .catch(error => {
                  console.error('Logout error:', error);
                  message.error('Failed to logout. Please try again.');
                });
            }}
          >
            <LogoutOutlined /> {!collapsed && 'Logout'}
          </div>
          {!collapsed && (
            <div
              className="sidebar-version"
              style={{ marginLeft: 'auto', fontSize: 11, color: '#999' }}
            >
              v{appVersion} · {process.env.REACT_APP_ENV || 'local'}
            </div>
          )}
        </div>
      )}
      title="LHPH.ai"
      menuExtraRender={() => {
        // On mobile/tablet, selectors are shown in menuContentRender (below profile)
        if (isMobileOrTablet) {
          return null;
        }

        // Check if we're on a company-related path with an ID
        const hasCompanyPath = companyPaths.some(path => segments.includes(path));
        const hasIdInUrl = segments.some(seg => /^\d+$/.test(seg));
        const showCompanySelector = hasCompanyPath && hasIdInUrl;

        return (
          <div>
            <SectionSelector collapsed={collapsed} />
            {showCompanySelector && <CompanySelector collapsed={collapsed} />}
          </div>
        );
      }}
      defaultCollapsed={collapsed}
      onCollapse={_collapsed => {
        if (isMobileOrTablet) {
          // On mobile, collapsed=true means drawer is closed (mobileMenuOpen=false)
          setMobileMenuOpen(!_collapsed);
        }
        setCollapsed(_collapsed);
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem('sidebar-collapsed', JSON.stringify(_collapsed));
        }
      }}
    >
      <PageContainer
        ghost
        header={{
          // Heading sits on its own line UNDER the breadcrumb trail (AntD's
          // PageHeader already stacks breadcrumb→title vertically). Render
          // it smaller so the breadcrumbs read as the primary nav and the
          // page heading is a subordinate label.
          title: headLoading ? (
            <Skeleton baseColor="#bfbaba" highlightColor="#f5f5f5" width={350} height={20} />
          ) : (
            <span
              style={{
                display: 'block',
                fontSize: 14,
                fontWeight: 500,
                color: 'rgba(0, 0, 0, 0.65)',
                lineHeight: 1.4,
                marginTop: 4,
              }}
            >
              {heading}
            </span>
          ),
          extra: extraContent,
          breadcrumb: (() => {
            // Pages that call setBreadcrumbs() win; everything else falls
            // back to a path-derived trail so the entire authenticated app
            // shares the same icon + dropdown pattern automatically.
            const effective =
              breadcrumbs.length > 0 ? breadcrumbs : deriveBreadcrumbs(location.pathname);
            if (effective.length === 0) return undefined;

            // The first crumb is the section root (e.g. "Loans"). Build it
            // once per render — every section-level crumb shows the same
            // sibling list. Sub-page items are built per crumb against the
            // section it belongs to.
            const sectionItems = buildSectionDropdownItems(routesConfig, {
              onNavigate: navigate,
            });
            const sectionPath = effective[0]?.path;
            const subPageItems = sectionPath
              ? buildSubPageDropdownItems(routesConfig, sectionPath, {
                  onNavigate: navigate,
                })
              : [];

            return {
              className: 'app-breadcrumb',
              items: effective.map((c, i) => {
                const isLast = i === effective.length - 1;
                // Crumbs whose title is already a ReactNode (e.g. an
                // embedded CompanySelector) own their own rendering — skip
                // the dropdown wrap so we don't double-wrap.
                const isStringTitle = typeof c.title === 'string';

                let body: React.ReactNode;
                if (isStringTitle && i === 0 && sectionItems.length > 0) {
                  // Section root → dropdown of sibling sections.
                  body = (
                    <span className="app-breadcrumb-item">
                      <BreadcrumbDropdown
                        label={
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            {c.icon && (
                              <span className="app-breadcrumb-icon">{c.icon}</span>
                            )}
                            <span className="app-breadcrumb-title">{c.title}</span>
                          </span>
                        }
                        items={sectionItems}
                        selectedKey={c.path}
                      />
                    </span>
                  );
                } else if (isStringTitle && i > 0 && subPageItems.length > 0) {
                  // Page-level crumb (intermediate or current) → dropdown
                  // of sibling pages within the same section.
                  body = (
                    <span className="app-breadcrumb-item">
                      <BreadcrumbDropdown
                        label={<span className="app-breadcrumb-title">{c.title}</span>}
                        items={subPageItems}
                        selectedKey={c.path}
                      />
                    </span>
                  );
                } else {
                  // Fallback / non-string titles (CompanySelector etc.)
                  // render exactly as before so existing behavior is
                  // preserved bit-for-bit.
                  const inner = (
                    <span className="app-breadcrumb-item">
                      {c.icon && <span className="app-breadcrumb-icon">{c.icon}</span>}
                      <span className="app-breadcrumb-title">{c.title}</span>
                    </span>
                  );
                  body = c.path && !isLast ? <Link to={c.path}>{inner}</Link> : inner;
                }

                return { title: body };
              }),
            };
          })(),
        }}
      >
        <div style={{ position: 'relative', minHeight: 600 }}>
          <Content
            style={{
              padding: 0,
              margin: 0,
              minHeight: 600,
              background: 'transparent', //colorBgContainer,
              borderRadius: borderRadiusLG,
            }}
          >
            <Outlet />
          </Content>
          <LoadingOverlay
            visible={isLoading}
            title={loadingMessage || 'Loading'}
            message="Please wait while we update your data..."
            type="overlay"
            contentOnly
          />
        </div>
      </PageContainer>
      <BugReporterButton />
    </ProLayout>
  );
};

export default MainLayout;
